const local = typeof window !== 'undefined' ? window.localStorage : null;
const session = typeof window !== 'undefined' ? window.sessionStorage : null;

// Storage keys for authentication (Admin-specific)
const AUTH_STORAGE_KEYS = new Set([
    'IdToken',
    'RefreshToken',
    'AccessToken',
    'email',
    'user_id',
    'id',
    'user_type',  // "super-admin" | "admin-user" | "corporate-user"
    'session_token',
    'custom:is_first_time_login',
    'user-info',
    'browser_trust_duration',
    'custom-status',
    'verify_otp_session_token',
    'preference_id',
    'token',
    'currentTimestamp',
    'session_ip_address',
    'session_ip_timestamp'
]);

// Event keys for cross-tab communication
const LOGOUT_EVENT_KEY = 'arcm_logout_event';
const FORCED_LOGOUT_EVENT_KEY = 'arcm_forced_logout_event';
const LOGIN_EVENT_KEY = 'arcm_login_event';

// Logout messages for different scenarios
export const LOGOUT_MESSAGES = {
    'expired': 'Your session has expired due to inactivity. Please log in again.',
    'deactivated': 'Your account has been deactivated.',
    'manual': 'You have been logged out due to logout in another tab',
    'duplicate': 'You have been logged out due to another login session'
};

// Session cookie: erased when browser is closed. Used to detect "new browser session" and clear persisted auth.
const SESSION_COOKIE_NAME = 'arcm_browser_session';

// Helper functions
const readItem = (storage, key) => {
    if (!storage) return null;
    try {
        return storage.getItem(key);
    } catch (error) {
        console.error(`Error reading ${key} from storage:`, error);
        return null;
    }
};

const setItem = (storage, key, value) => {
    if (!storage) return;
    try {
        storage.setItem(key, value);
    } catch (error) {
        console.error(`Error setting ${key} in storage:`, error);
    }
};

const removeItem = (storage, key) => {
    if (!storage) return;
    try {
        storage.removeItem(key);
    } catch (error) {
        console.error(`Error removing ${key} from storage:`, error);
    }
};

// Session cookie helpers (cookie is session-lived: removed when browser closes)
const getSessionCookie = () => {
    if (typeof document === 'undefined' || !document.cookie) return false;
    return document.cookie.split(';').some((part) => part.trim().startsWith(SESSION_COOKIE_NAME + '='));
};

const setSessionCookie = () => {
    if (typeof document === 'undefined') return;
    try {
        document.cookie = `${SESSION_COOKIE_NAME}=1; path=/; SameSite=Lax`;
    } catch (error) {
        console.error('Error setting session cookie:', error);
    }
};

const clearSessionCookie = () => {
    if (typeof document === 'undefined') return;
    try {
        document.cookie = `${SESSION_COOKIE_NAME}=; path=/; Max-Age=0`;
    } catch (error) {
        console.error('Error clearing session cookie:', error);
    }
};

/**
 * Call before app renders. If the session cookie is missing (browser was closed and reopened),
 * clear auth storage so the user is not left with stale tokens and a blank or broken screen.
 */
export const clearStorageIfNewBrowserSession = () => {
    if (typeof window === 'undefined' || !local || !session) return;

    if (!getSessionCookie()) {
        clearAuthSession();
    }
};

// Get auth item (checks both storages)
export const getAuthItem = (key) => {
    const sessionValue = readItem(session, key);
    if (sessionValue !== null && sessionValue !== undefined && sessionValue !== '') {
        return sessionValue;
    }
    return readItem(local, key);
};

// Set auth item (sets in both storages)
export const setAuthItem = (key, value) => {
    setItem(session, key, value);
    setItem(local, key, value);
};

// Remove auth item (removes from both storages)
export const removeAuthItem = (key) => {
    removeItem(session, key);
    removeItem(local, key);
};

// Persist auth session (sets all auth keys)
export const persistAuthSession = (authData) => {
    if (!local || !session) return;

    Object.entries(authData).forEach(([key, value]) => {
        if (AUTH_STORAGE_KEYS.has(key) || key.startsWith('custom:')) {
            const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
            setItem(session, key, stringValue);
            setItem(local, key, stringValue);
        }
    });
};

// Persist auth session to sessionStorage ONLY (no localStorage, no cross-tab events)
// Used during OTP flow before "Trust This Browser?" modal decision
export const persistAuthSessionToSession = (authData) => {
    if (!session) return;

    Object.entries(authData).forEach(([key, value]) => {
        if (AUTH_STORAGE_KEYS.has(key) || key.startsWith('custom:')) {
            const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
            setItem(session, key, stringValue);
        }
    });
};

// Sync localStorage from sessionStorage (copies auth keys from session → local)
// Called after user completes "Trust This Browser?" modal, right before broadcastLogin()
export const syncLocalStorageFromSession = () => {
    if (!local || !session) return;

    AUTH_STORAGE_KEYS.forEach((key) => {
        const sessionValue = readItem(session, key);
        if (sessionValue !== null && sessionValue !== undefined && sessionValue !== '') {
            setItem(local, key, sessionValue);
        }
    });
};

// Clear auth session (removes all auth keys)
export const clearAuthSession = () => {
    if (!local || !session) return;

    AUTH_STORAGE_KEYS.forEach((key) => {
        removeItem(session, key);
        removeItem(local, key);
    });

    // Also clear custom keys
    Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('custom:')) {
            removeItem(local, key);
        }
    });
    Object.keys(sessionStorage).forEach((key) => {
        if (key.startsWith('custom:')) {
            removeItem(session, key);
        }
    });

    // Clear logout reason to prevent stale messages on future logouts
    removeItem(local, 'arcm_logout_reason');
    removeItem(session, 'arcm_logout_reason');

    clearSessionCookie();
};

// Validate and clean up stale/invalid auth data
// This prevents refresh loops caused by corrupted or expired tokens
export const validateAndCleanAuthSession = () => {
    if (!local || !session) {
        return false; // No storage available
    }

    const idToken = readItem(local, 'IdToken') || readItem(session, 'IdToken');
    
    // If no token, session is clean (not logged in)
    if (!idToken) {
        return true; // Valid state - no auth data
    }

    // Check for corrupted token format (JWT tokens should have 3 parts separated by dots)
    // Only clear if token format is clearly invalid, not just old
    if (idToken && typeof idToken === 'string') {
        const parts = idToken.split('.');
        // Valid JWT should have exactly 3 parts and be reasonably long
        if (parts.length !== 3 || idToken.length < 50) {
            // Token doesn't look like a valid JWT - likely corrupted
            console.warn('Invalid/corrupted token format detected, clearing to prevent loops');
            clearAuthSession();
            return false; // Session was cleared
        }
        
        // Check if token parts are empty or too short (corrupted)
        if (parts.some(part => !part || part.length < 10)) {
            console.warn('Corrupted token detected (empty parts), clearing to prevent loops');
            clearAuthSession();
            return false; // Session was cleared
        }
    }

    return true; // Session appears valid
};

// Sync session from localStorage to sessionStorage
export const syncSessionFromLocalStorage = () => {
    if (!local || !session) {
        return;
    }

    AUTH_STORAGE_KEYS.forEach((key) => {
        const hasSessionValue = readItem(session, key);
        if (hasSessionValue === null || hasSessionValue === undefined || hasSessionValue === '') {
            const localValue = readItem(local, key);
            if (localValue !== null && localValue !== undefined) {
                setItem(session, key, localValue);
            }
        }
    });
};

// Broadcast logout event to other tabs
// reason: 'manual' (user clicked logout), 'expired' (token expired), 'deactivated' (account deactivated)
export const broadcastLogout = (reason = 'manual') => {
    setItem(local, 'arcm_logout_reason', reason);
    setItem(local, LOGOUT_EVENT_KEY, Date.now().toString());
};

// Broadcast WS-forced logout (duplicate session from another device).
// Sibling old-session tabs receive this and show the "another login session" toast
// instead of the "logged out in another tab" toast.
export const broadcastForcedLogout = () => {
    setItem(local, FORCED_LOGOUT_EVENT_KEY, Date.now().toString());
};

// Broadcast login event to other tabs
export const broadcastLogin = () => {
    setSessionCookie();
    setItem(local, LOGIN_EVENT_KEY, Date.now().toString());
};

// Subscribe to auth events (login/logout) from other tabs
export const subscribeToAuthEvents = (handler) => {
    if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
        return () => { };
    }

    const listener = (event) => {
        if (event.key === LOGOUT_EVENT_KEY && event.newValue) {
            handler('logout');
        }
        if (event.key === FORCED_LOGOUT_EVENT_KEY && event.newValue) {
            handler('forced_logout');
        }
        if (event.key === LOGIN_EVENT_KEY && event.newValue) {
            handler('login');
        }
    };

    window.addEventListener('storage', listener);
    return () => window.removeEventListener('storage', listener);
};